import { useState, useEffect } from "react";
import API_URL from "../const";
import { trackMetaEvent } from "../utils/metaEvents";
import Spinner from "./Spinner/Spinner";
import Toast from "./Toast/Toast";
import Headers from "./HeaderPublic";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import logo from "../assets/img/logotipo.png";
import flechas from "../assets/img/Flechas.jpeg";

const YOUTUBE_VIDEO_ID = "u7K8OE2l4yE";

const fullWidthStyle = {
  width: "100vw",
  position: "relative",
  left: "50%",
  right: "50%",
  marginLeft: "-50vw",
  marginRight: "-50vw",
  overflowX: "hidden",
};

const requiredFieldStyle = {
  color: "red",
  marginLeft: "2px",
};

function Contact() {
  const navigate = useNavigate();
  const [IsOpenSpinner, setIsOpenSpinner] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    apellidos: "",
    email: "",
    numero: "",
    empresa: "",
    giro: "",
    puesto: "",
    mensaje: "",
    cargo: "",
    numero_colaboradores: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // El PageView se dispara de forma deduplicada desde usePageTracking (hook global).
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsOpenSpinner(true);

    try {
      const response = await fetch(`${API_URL}insertContactanos.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData),
      });

      if (!response.ok) {
        throw new Error(response.statusText || "Error desconocido en la respuesta");
      }

      const text = await response.text();

      if (!text || text.trim() === "") {
        throw new Error("El servidor devolvió una respuesta vacía");
      }

      // Intentar parsear JSON (directo o extrayendo del texto)
      try {
        JSON.parse(text);
      } catch {
        const startIndex = text.indexOf("[{");
        if (startIndex === -1) {
          throw new Error("No se encontró un formato JSON válido en la respuesta");
        }
        JSON.parse(text.substring(startIndex));
      }

      setIsOpenSpinner(false);

      // Conversión: evento Lead deduplicado (navegador Pixel + Conversions API).
      trackMetaEvent("Lead", {
        email: formData.email,
        phone: formData.numero,
        customData: { content_name: "Contacto" },
      });

      navigate("/contact-success", {
        state: {
          nombre: formData.nombre,
          email: formData.email,
          mensaje: "Datos enviados correctamente",
        },
      });
    } catch (error) {
      console.error("Error en la solicitud:", error);
      setError(error.message || "Ocurrió un error al enviar el formulario");
      setIsOpenSpinner(false);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  return (
    <>
      {/* Header de ancho completo */}
      <div style={fullWidthStyle}>
        <Headers />
      </div>

      {/* Contenido principal */}
      <section
        id="contact"
        className="py-8 sm:py-12 md:py-16 w-full mt-8 sm:mt-12"
      >
        <Spinner isOpen={IsOpenSpinner} />
        {showError && (
          <Toast
            isOpen={true}
            title="Error"
            text={error || "Se ha producido un error"}
          />
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Columna izquierda - Logo y Video */}
            <div className="flex flex-col items-center lg:items-start space-y-6 sm:space-y-8">
              {/* Logo con texto */}
              <div className="flex items-center">
                <img
                  src={logo}
                  alt="Industrial Business Clusters"
                  className="h-16 sm:h-20 md:h-24 w-auto object-contain"
                />
                <div className="ml-3 sm:ml-4">
                  <h2 className="text-sm sm:text-base md:text-lg font-bold text-[#29265b] leading-tight tracking-wide">
                    INDUSTRIAL
                    <br />
                    BUSINESS
                    <br />
                    CLUSTERS
                  </h2>
                </div>
              </div>

              {/* Video de YouTube */}
              <div className="w-full aspect-video bg-[#2a3180] rounded-lg overflow-hidden shadow-lg">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}`}
                  title="Industrial Business Clusters"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>

              {/* Texto y flechas - Solo visible en desktop */}
              <div className="hidden lg:flex flex-col items-center space-y-6 mt-8">
                {/* Texto de beneficios */}
                <div className="text-center">
                  <p className="text-[#29265b] font-bold text-xl leading-tight">
                    SÉ PARTE DE TODOS LOS BENEFICIOS QUE
                    <br />
                    INDUSTRIAL BUSINESS CLUSTER
                    <br />
                    <span className="text-[#1e72b9] text-xl">TE OFRECE</span>
                  </p>
                </div>

                {/* Texto y flechas */}
                <div className="flex items-center space-x-4">
                  <p className="text-[#1e72b9] font-bold text-2xl">
                    ENVÍA TUS DATOS AQUÍ
                  </p>
                  {/* Imagen de flechas */}
                  <img
                    src={flechas}
                    alt="Flechas"
                    className="h-24 w-auto object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Columna derecha - Formulario */}
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="text-center mb-6 sm:mb-8">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1e72b9] flex items-center justify-center">
                  <i className="bx bxs-chat text-4xl md:text-5xl"></i>
                  <span className="ml-2 sm:ml-3 text-2xl sm:text-3xl md:text-4xl">
                    Contacta con nosotros
                  </span>
                </h1>
                <p className="text-gray-500 mt-2 text-base sm:text-lg">
                  Completa el formulario para crear tu solicitud
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {/* Nombre y Apellido Paterno */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label
                      htmlFor="nombre"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Nombre<span style={requiredFieldStyle}>*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-id-card text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="text"
                        name="nombre"
                        id="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Tu nombre"
                        required
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="apellidos"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Apellido paterno<span style={requiredFieldStyle}>*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-id-card text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="text"
                        name="apellidos"
                        id="apellidos"
                        value={formData.apellidos}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Apellido paterno"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Correo y Teléfono */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label
                      htmlFor="email"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Correo electrónico
                      <span style={requiredFieldStyle}>*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-envelope text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="correo@ejemplo.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="numero"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Número telefónico<span style={requiredFieldStyle}>*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-phone text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="text"
                        name="numero"
                        id="numero"
                        value={formData.numero}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="+52 (123) 456-7890"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Empresa y Giro */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label
                      htmlFor="empresa"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Nombre de la empresa
                      <span style={requiredFieldStyle}>*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-building text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="text"
                        name="empresa"
                        id="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Nombre de la empresa"
                        required
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="giro"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Giro de la empresa
                      <span style={requiredFieldStyle}>*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-category text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="text"
                        name="giro"
                        id="giro"
                        value={formData.giro}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Giro empresarial"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Puesto y Cargo */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label
                      htmlFor="puesto"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Puesto<span style={requiredFieldStyle}>*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-briefcase text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="text"
                        name="puesto"
                        id="puesto"
                        value={formData.puesto}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Tu puesto"
                        required
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="numero_colaboradores"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Número de colaboradores
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                        <i className="bx bx-group text-lg text-gray-500"></i>
                      </span>
                      <input
                        type="number"
                        name="numero_colaboradores"
                        id="numero_colaboradores"
                        value={formData.numero_colaboradores}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Ej. 50"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Mensaje */}
                <div className="relative">
                  <label
                    htmlFor="mensaje"
                    className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                  >
                    Mensaje
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-2 sm:px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg h-20">
                      <i className="bx bx-message text-lg text-gray-500"></i>
                    </span>
                    <textarea
                      name="mensaje"
                      id="mensaje"
                      rows="3"
                      placeholder="Cuéntanos que necesitas"
                      value={formData.mensaje}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none h-20"
                    ></textarea>
                  </div>
                </div>

                {/* Botón enviar */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={IsOpenSpinner}
                    className={`w-full text-white font-medium py-2.5 sm:py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center text-sm sm:text-base ${
                      IsOpenSpinner
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-[#0092ff] hover:bg-[#1e72b9]"
                    }`}
                  >
                    <i className="bx bx-send text-lg"></i>
                    <span className="ml-2">
                      {IsOpenSpinner ? "Enviando..." : "Enviar solicitud"}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Sección de redes sociales */}
      <section className="py-8 sm:py-12 text-center px-4">
        <h3 className="text-[#29265b] font-bold text-xl sm:text-2xl md:text-3xl mb-3 sm:mb-4">
          Síguenos en redes
        </h3>
        <p className="text-[#29265b] text-sm sm:text-base md:text-lg mb-6 sm:mb-8">
          Sé parte de una comunidad que conecta y expande negocios.
        </p>

        <div className="flex justify-center flex-wrap gap-3 sm:gap-4 md:gap-6">
          <a
            href="https://www.facebook.com/share/1B9a1pXeXt/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#39a8e0] hover:bg-[#1e72b9] transition-colors rounded-xl w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center"
          >
            <svg
              className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </a>

          <a
            href="https://www.instagram.com/industrial_business_clusters?igsh=MWRjN2VlaHp4M3p0Mw=="
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#39a8e0] hover:bg-[#1e72b9] transition-colors rounded-xl w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center"
          >
            <svg
              className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </a>

          <a
            href="https://www.youtube.com/@Industrial_bunsiness_Clusters"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#39a8e0] hover:bg-[#1e72b9] transition-colors rounded-xl w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center"
          >
            <svg
              className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
          </a>

          <a
            href="https://www.linkedin.com/company/industrial-business-clusters/?viewAsMember=true"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#39a8e0] hover:bg-[#1e72b9] transition-colors rounded-xl w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center"
          >
            <svg
              className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        </div>
      </section>

      {/* Footer de ancho completo */}
      <div style={fullWidthStyle} className="-mb-8">
        <Footer />
      </div>
    </>
  );
}

export default Contact;
